/* eslint-disable react/prop-types */
import React, { useMemo, useState } from "react";
import { PostFieldLabel, PostFieldCounter } from "components/Posts/PostForm";
import PostFieldError from "components/Posts/PostForm/PostFieldError/PostFieldError";
import { countCharacter } from "utils/strings";
import { FIELDS } from "constants/PostFormFields";
import { SkedCaption, useSkedCaptionAutocomplete } from "ui/SkedCaption";
import { postPlatformsConfig, RULES } from "../postPlatforms.config";
import { useDebouncedCallback } from "use-debounce";
import {
  SkedCaptionV2,
  SkedEditorHistory,
} from "../../../../ui/SkedCaption/v2/sked-caption-v2";
import { localStorageFeatureToggle } from "../../../../utils/feature-toggle-utils";
import { useSelector } from "react-redux";
import { getDescriptions } from "../../../../selectors/postFormSelectors";
import { PlatformSpec } from "../../../../constants/Platforms";
import {
  FormValidationErrors,
  FormValidationWarnings,
} from "../../../../shared/types/post";
import {
  AccountPlatformType,
  PostLinkedInAccountType,
} from "../../../../shared/types/accounts";

const getMaxMentionCount = (platformType?: AccountPlatformType | "LINKED") => {
  if (!platformType) {
    return;
  }
  return postPlatformsConfig?.[platformType]?.validation?.error?.[
    FIELDS.CAPTION
  ]?.[RULES.MENTION_COUNT]?.[1];
};

interface Props {
  platforms?: PlatformSpec[];
  selectedPlatform: AccountPlatformType | "LINKED";
  value: string;
  className?: string;
  label: React.ReactNode;
  disabled?: boolean;
  onChange: (value: string) => void;
  maxLength: number;
  hashtagCount?: number;
  errors?: FormValidationErrors;
  warnings?: FormValidationWarnings;
  hasMoreThanOnelinkedInAccount?: boolean;
  selectedLinkedInAccountType?: PostLinkedInAccountType;
  selectedLinkedUrn?: string;
  disableMention?: boolean;
  formId?: string;
  captionEditHistory?: SkedEditorHistory;
}

export default function CaptionField(props: Props) {
  // eslint-disable-next-line react/prop-types
  const {
    platforms = [],
    selectedPlatform,
    value,
    className,
    label,
    disabled,
    onChange,
    maxLength,
    hashtagCount,
    errors,
    warnings,
    hasMoreThanOnelinkedInAccount,
    selectedLinkedInAccountType,
    selectedLinkedUrn,
    disableMention,
    formId,
    captionEditHistory,
  } = props;
  const descriptionsLength = useSelector((state) =>
    getDescriptions(state, props.formId)
  ).length;
  const mentionCount = countCharacter(value, "@");
  const debouncedOnChange = useDebouncedCallback((newValue) => {
    if (value !== newValue) {
      onChange(newValue);
    }
  }, 200);

  const maxMentionCount = useMemo(() => {
    const isMultiplyPlatform = platforms.length > 1;

    return isMultiplyPlatform
      ? platforms.reduce((minCount, { TYPE }) => {
          const max = getMaxMentionCount(TYPE);

          if (minCount === undefined || minCount > max) {
            return max;
          }

          return minCount;
        }, undefined)
      : getMaxMentionCount(selectedPlatform);
  }, [platforms, selectedPlatform]);

  const skedCaptionAutocomplete = useSkedCaptionAutocomplete(selectedPlatform);
  const [captionV2] = useState(localStorageFeatureToggle.get("CaptionV2"));

  return (
    <div data-testid="postCaption" className={className}>
      <div className="mb-2">
        <PostFieldLabel className="flex items-center">{label}</PostFieldLabel>
      </div>
      {captionV2 ? (
        <SkedCaptionV2
          initialValue={value}
          disabled={disabled}
          onChange={debouncedOnChange}
          suggestions={true}
          autocomplete={skedCaptionAutocomplete}
          selectedPlatform={selectedPlatform}
          hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
          selectedLinkedInAccountType={selectedLinkedInAccountType}
          selectedLinkedUrn={selectedLinkedUrn}
          formId={formId}
          editorHistory={captionEditHistory}
          descriptionsDisabled={descriptionsLength <= 0}
          counters={{
            characters: typeof value === "string" ? value.length : 0,
            charactersMax: maxLength,
            hashtags: hashtagCount,
            hashtagsMax: 30,
            mentions:
              !disableMention && mentionCount > 0 ? mentionCount : undefined,
            mentionsMax:
              selectedPlatform !== "LI" && maxMentionCount
                ? maxMentionCount
                : 0,
          }}
        />
      ) : (
        <>
          <SkedCaption
            initialValue={value}
            disabled={disabled}
            onChange={debouncedOnChange}
            suggestions={true}
            upcomingEditPopup={true}
            autocomplete={skedCaptionAutocomplete}
            selectedPlatform={selectedPlatform}
            hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
            selectedLinkedInAccountType={selectedLinkedInAccountType}
            disableMention={disableMention}
            selectedLinkedUrn={selectedLinkedUrn}
            formId={formId}
          />
          <div className="flex">
            <PostFieldCounter
              count={value ? value.length : 0}
              max={maxLength}
              className="flex-grow text-right mt-2 mr-8"
            >
              Characters
            </PostFieldCounter>
            <PostFieldCounter
              count={hashtagCount}
              max={30}
              className="text-right mt-2"
            >
              Hashtags
            </PostFieldCounter>
            {!disableMention && mentionCount > 0 && (
              <PostFieldCounter
                count={mentionCount}
                max={maxMentionCount ? maxMentionCount : 0}
                className="text-right mt-2 ml-8"
                selectedPlatform={selectedPlatform}
              >
                Mentions
              </PostFieldCounter>
            )}
          </div>
        </>
      )}

      <PostFieldError
        className="mt-4"
        fields={[FIELDS.CAPTION]} // todo: move it
        errors={errors}
        warnings={warnings}
      />
    </div>
  );
}
